import { c as _c } from "react/compiler-runtime";
import { SearchTextField } from "@components/common/search-text-field";
import { TypedEvent } from "@faro-lotv/foundation";
import { QUICK_FILTER } from "@hooks/table-filters/use-filter-query-params";
import { useDebouncedSearchText } from "@hooks/use-debounced-search-text";
import { useMediaQueryList } from "@hooks/use-media-query";
import { Box, ClickAwayListener, Stack } from "@mui-wrapper";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { tableTypeSelector } from "@store/table/table-selector";
import { setDebouncedSearchText } from "@store/ui/ui-slice";
import { SearchIcon, Text } from "@flat-ui";
import { TableType } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

/** Event to clear the table search */
export const onClearTableSearch = new TypedEvent();

/** Handling the quick search for filtering the table */
export function FilterToolbarQuickSearch() {
  const $ = _c(30);
  const {
    isScreenXlOrLarger,
    isScreenSmOrLarger,
    isScreenSmOrSmaller,
    isScreenXs
  } = useMediaQueryList();
  const [searchParams] = useSearchParams();
  let t0;
  if ($[0] !== searchParams) {
    t0 = searchParams.get(QUICK_FILTER) ?? "";
    $[0] = searchParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [searchText, setSearchText] = useState(t0);
  let t1;
  if ($[2] !== searchText) {
    t1 = {
      searchText
    };
    $[2] = searchText;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    debouncedSearchText
  } = useDebouncedSearchText(t1);
  const [shouldShowSearchBox, setShouldShowSearchBox] = useState(searchText !== "");
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);
  const {
    trackEvent
  } = useTrackEvent();
  const tableType = useAppSelector(tableTypeSelector);
  const dispatch = useAppDispatch();
  const isAlwaysShown = tableType === TableType.projectMarkups;
  let t2;
  let t3;
  if ($[4] !== debouncedSearchText || $[5] !== dispatch) {
    t2 = () => {
      dispatch(setDebouncedSearchText(debouncedSearchText));
      return () => {
        dispatch(setDebouncedSearchText(""));
      };
    };
    t3 = [debouncedSearchText, dispatch];
    $[4] = debouncedSearchText;
    $[5] = dispatch;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  bb0: {
    if (isScreenXlOrLarger) {
      t4 = isAlwaysShown ? "360px" : "500px";
      break bb0;
    }
    if (isScreenSmOrLarger) {
      t4 = "300px";
      break bb0;
    }
    if (isScreenSmOrSmaller) {
      t4 = "100%";
      break bb0;
    }
    t4 = undefined;
  }
  const searchWidth = t4;
  const placeHolder = tableType === TableType.projectMarkups ? "Search annotations" : "Search";
  let t5;
  if ($[8] !== isTextFieldFocused || $[9] !== searchText || $[10] !== tableType || $[11] !== trackEvent) {
    t5 = function onClickAway() {
      if (isTextFieldFocused) {
        if (searchText.length === 0) {
          setShouldShowSearchBox(false);
        } else {
          trackEvent({
            name: `filter ${tableType}`,
            props: {
              filterBy: "filter search",
              textLength: searchText.length
            }
          });
        }
        setIsTextFieldFocused(false);
      }
    };
    $[8] = isTextFieldFocused;
    $[9] = searchText;
    $[10] = tableType;
    $[11] = trackEvent;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const onClickAway = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = function onClearClicked() {
      setShouldShowSearchBox(false);
      setSearchText("");
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const onClearClicked = t6;
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const disposer = onClearTableSearch.on(onClearClicked);
      return () => disposer.dispose();
    };
    t8 = [];
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  useEffect(t7, t8);
  if (shouldShowSearchBox || isScreenXs || isAlwaysShown) {
    let t9;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = text => {
        setIsTextFieldFocused(true);
        setSearchText(text);
      };
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== searchWidth) {
      t10 = {
        width: searchWidth
      };
      $[17] = searchWidth;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    let t11;
    if ($[19] !== placeHolder || $[20] !== searchText || $[21] !== t10) {
      t11 = <Box component="div"><SearchTextField placeholder={placeHolder} searchText={searchText} onClearClicked={onClearClicked} onChangeSearch={t9} sx={t10} /></Box>;
      $[19] = placeHolder;
      $[20] = searchText;
      $[21] = t10;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== onClickAway || $[24] !== t11) {
      t12 = <ClickAwayListener onClickAway={onClickAway}>{t11}</ClickAwayListener>;
      $[23] = onClickAway;
      $[24] = t11;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    return t12;
  }
  let t9;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => setShouldShowSearchBox(true);
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      cursor: "pointer",
      p: "6px 10px",
      "&:hover": {
        backgroundColor: "action.hover",
        color: "primary.main"
      },
      alignItems: "center"
    };
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <SearchIcon sx={{
      width: "18px",
      height: "18px"
    }} />;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Stack direction="row" onClick={t9} sx={t10}>{t11}<Text variant="heading12" sx={{
        color: "inherit",
        ml: "8px"
      }}>Search</Text></Stack>;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  return t12;
}